import React, { useState, useEffect } from 'react';
import { Modal, Drawer, Space, Row, Col, Spin, Dropdown, Button, message, Tag, Tooltip } from 'antd';
import HeartLoader from '../../components/shared/HeartLoader';
import {
  UserOutlined,
  EditOutlined,
  TeamOutlined,
  DownOutlined,
  LinkOutlined,
  SaveOutlined,
  TagOutlined,
  CheckCircleFilled,
  MessageOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { COLORS } from '../../constants/colors';
import PatientService from '../../services/PatientService';
import api from '../../services/api';
import { useStore } from '../../hooks/storeContext';
import PatientTokenService from '../../services/PatientTokenService';
import UserManagementService from '../../services/UserManagementService';
import { householdIncomeLabels, insuranceCoverageLabels, getLabel } from '../../utils/labelMappings';
import NeedsAttentionModal from './components/NeedsAttentionModal';
import {
  PatientDetailsModalStyles,
  ModalTitle,
  ModalButton,
  PatientHeaderSection,
  PatientIdLabel,
  TestingCodeCell,
  TestingCodeInput,
  TestingCodeSaveButton,
  ModalContent
} from './styled';

// Pre-configured "ready for screening" SMS. The Black Heart Association SMS
// number is approved and active.
const SMS_NOTIFY_ENABLED = true;
const READY_MESSAGE = 'We are ready to start your screening, please head to the screening area.';

const PatientDetailsModal = ({ visible, onCancel, patient, onEditPatient, onStatusChange, onSaved }) => {
  const { authStore } = useStore();
  const canMessage = authStore.isAdmin || authStore.hasPermission('messaging');
  const [fullPatientData, setFullPatientData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNeedsAttentionModal, setShowNeedsAttentionModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [staffName, setStaffName] = useState(null);
  const [copyingLink, setCopyingLink] = useState(false);
  const [testingCodeDraft, setTestingCodeDraft] = useState('');
  const [savingTestingCode, setSavingTestingCode] = useState(false);
  const [sendingNotify, setSendingNotify] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Default timezone for displaying dates (Central Time - Texas)
  const DEFAULT_TIMEZONE = 'America/Chicago';
  
  // Get timezone from event type, default to Central Time
  const getTimezone = () => patient?.eventType?.timezone || DEFAULT_TIMEZONE;

  // Load full patient data when modal opens
  useEffect(() => {
    if (visible && patient) {
      loadFullPatientData();
    }
  }, [visible, patient]);

  const loadStaffData = async (staffId) => {
    try {
      console.log('Loading staff data for ID:', staffId);
      const response = await UserManagementService.getUserById(staffId);
      console.log('Staff data response:', response);
      
      if (response.success && response.data) {
        // The API returns fullName as a single field, not firstName/lastName
        const staffName = response.data.fullName || 
                          `${response.data.firstName || ''} ${response.data.lastName || ''}`.trim() ||
                          'Staff Member';
        console.log('Setting staff name to:', staffName);
        setStaffName(staffName);
      } else {
        console.log('Staff data not found or invalid response');
        setStaffName('Staff Member');
      }
    } catch (error) {
      console.error('Error loading staff data:', error);
      setStaffName('Staff Member');
    }
  };

  const loadFullPatientData = async () => {
    if (!patient?.patient?.patientId && !patient?.patientId) return;
    
    setLoading(true);
    try {
      // Try to get full patient data from backend using PatientService
      const patientId = patient.patient?.patientId || patient.patientId;
      const response = await PatientService.getPatientDetails(patientId);
      setFullPatientData(response.data);
      setTestingCodeDraft(response.data?.testingCode || '');
      
      // Load staff information if assignedStaff exists
      if (response.data?.queueInfo?.assignedStaff) {
        console.log('Found assignedStaff:', response.data.queueInfo.assignedStaff);
        await loadStaffData(response.data.queueInfo.assignedStaff);
      } else {
        console.log('No assignedStaff found in queueInfo');
        setStaffName(null);
      }
    } catch (error) {
      console.error('Error loading full patient data:', error);
      // Fall back to existing patient data
      const fallback = patient.patient || patient;
      setFullPatientData(fallback);
      setTestingCodeDraft(fallback?.testingCode || '');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyReady = async (phoneNumber) => {
    if (!SMS_NOTIFY_ENABLED) return;
    if (!phoneNumber) {
      message.error('No phone number on file for this patient');
      return;
    }
    setSendingNotify(true);
    try {
      const contactName = `${fullPatientData?.firstName || ''} ${fullPatientData?.lastName || ''}`.trim() || undefined;
      await api.post('/inbox/conversations/new/messages', {
        text: READY_MESSAGE,
        phoneNumber,
        contactName
      });
      message.success('Patient notified that you are ready');
    } catch (error) {
      console.error('Error sending ready notification:', error);
      message.error('Failed to send notification');
    } finally {
      setSendingNotify(false);
    }
  };

  const handleSaveTestingCode = async () => {
    const patientId = fullPatientData?.patientId || patient?.patient?.patientId || patient?.patientId;
    if (!patientId) {
      message.error('Unable to save: missing patient ID');
      return;
    }
    const trimmed = (testingCodeDraft || '').trim();
    if (trimmed === (fullPatientData?.testingCode || '')) {
      return; // No change
    }
    setSavingTestingCode(true);
    try {
      await PatientService.updatePatient(patientId, { testingCode: trimmed });
      setFullPatientData((prev) => ({ ...(prev || {}), testingCode: trimmed }));
      message.success('Testing code saved');
      // Refresh the queue so the new code shows in the table immediately
      if (onSaved) onSaved(patientId, trimmed);
    } catch (err) {
      console.error('Error saving testing code:', err);
      message.error('Failed to save testing code');
    } finally {
      setSavingTestingCode(false);
    }
  };

  if (!patient) return null;

  const getStatusColor = (status) => {
    const colors = {
      new: COLORS.warningAmber,
      waiting: COLORS.warningAmber,
      in_progress: COLORS.$primaryBlue,
      completed: COLORS.healthyGreen,
      cancelled: COLORS.criticalRed,
      archived: COLORS.textMuted,
      'no-show': COLORS.criticalRed
    };
    return colors[status] || COLORS.textMuted;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: COLORS.$alertRed,
      high: COLORS.$alertRed,
      medium: COLORS.warningAmber,
      low: COLORS.healthyGreen,
      normal: COLORS.$primaryBlue
    };
    return colors[priority?.toLowerCase()] || COLORS.textMuted;
  };

  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: getTimezone()
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: getTimezone()
    });
  };
  
  const handleEditPatient = () => {
    if (onEditPatient) {
      onEditPatient(patient);
    }
    onCancel(); // Close the details modal
  };

  const handleStatusChangeClick = (newStatus) => {
    if (newStatus === 'needs_attention') {
      // Store the pending status change and show the notes modal
      setPendingStatusChange(newStatus);
      setShowNeedsAttentionModal(true);
    } else {
      // For other status changes, proceed directly
      if (onStatusChange) {
        onStatusChange(patient, newStatus);
      }
      // Note: Modal will be closed by parent component's handleStatusChange
    }
  };

  const handleNeedsAttentionSave = (notes) => {
    // Close the notes modal
    setShowNeedsAttentionModal(false);
    
    // Execute the pending status change with notes
    if (onStatusChange && pendingStatusChange) {
      onStatusChange(patient, pendingStatusChange, notes);
    }
    
    // Clear pending state
    setPendingStatusChange(null);
    // Note: Modal will be closed by parent component's handleStatusChange
  };

  const handleNeedsAttentionCancel = () => {
    setShowNeedsAttentionModal(false);
    setPendingStatusChange(null);
  };

  // Copy Patient Link — generates a secure token link for patient to complete deferred fields
  const handleCopyPatientLink = async () => {
    const patientId = patientData?.patientId || patient?.patient?.patientId || patient?.patientId;
    const visitId = queueData?._id;
    const bookingId = queueData?.bookingId;

    if (!patientId || !visitId) {
      message.error('Cannot generate link — missing patient or visit data');
      return;
    }

    setCopyingLink(true);
    try {
      const result = await PatientTokenService.generateToken(patientId, visitId, bookingId);
      if (result?.url) {
        await navigator.clipboard.writeText(result.url);
        message.success('Patient link copied to clipboard!');
      } else {
        message.error('Failed to generate patient link');
      }
    } catch (err) {
      console.error('Error generating patient link:', err);
      message.error('Failed to generate patient link');
    } finally {
      setCopyingLink(false);
    }
  };

  // Derive patientData for simpler access
  const patientData = fullPatientData || patient.patient || patient;
  const queueData = fullPatientData?.queueInfo || patient.queueInfo || patient;

  const statusMenuItems = [
    ...(queueData.status !== 'pre_registered'
      ? [{
          key: 'pre_registered',
          label: 'Move to Pre-Registered',
          style: { color: '#722ed1' },
          onClick: () => handleStatusChangeClick('pre_registered'),
        }]
      : []),
    ...(queueData.status !== 'new' && queueData.status !== 'new_patient'
      ? [{
          key: 'new_patient',
          label: 'Set as New Patient',
          onClick: () => handleStatusChangeClick('new_patient'),
        }]
      : []),
    ...(queueData.status !== 'in_progress'
      ? [{
          key: 'in_progress',
          label: 'Set In Progress',
          onClick: () => handleStatusChangeClick('in_progress'),
        }]
      : []),
    ...(queueData.status !== 'completed' && ['new', 'new_patient', 'in_progress', 'pending_review', 'matched', 'duplicate'].includes(queueData.status)
      ? [{
          key: 'completed',
          label: 'Mark Completed',
          onClick: () => handleStatusChangeClick('completed'),
        }]
      : []),
    ...(queueData.status !== 'archived'
      ? [{
          key: 'archived',
          label: 'Archive',
          danger: true,
          onClick: () => handleStatusChangeClick('archived'),
        }]
      : []),
    ...(queueData.status !== 'needs_attention'
      ? [{
          key: 'needs_attention',
          label: 'Needs Attention',
          onClick: () => handleStatusChangeClick('needs_attention'),
        }]
      : []),
    ...(queueData.status !== 'no-show'
      ? [{
          key: 'no_show',
          label: 'Mark No Show',
          danger: true,
          onClick: () => handleStatusChangeClick('no-show'),
        }]
      : []),
  ];

  // ─── Compact info row helper (label + value, no rainbow icons) ─────
  const InfoItem = ({ label, value, mono = false }) => (
    <div>
      <div style={{
        fontSize: '11px',
        fontWeight: 600,
        color: COLORS.textMuted,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 4
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '14px',
        fontWeight: 500,
        color: COLORS.textDark,
        fontFamily: mono ? 'Monaco, Consolas, "Liberation Mono", monospace' : undefined,
        wordBreak: 'break-word'
      }}>
        {value || <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>—</span>}
      </div>
    </div>
  );

  const sectionCard = {
    background: '#ffffff',
    border: '1px solid #eef2f7',
    borderRadius: '10px',
    padding: '16px 18px',
    marginBottom: '12px'
  };
  const sectionHeading = {
    margin: '0 0 14px 0',
    fontSize: '13px',
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.6px'
  };

  const titleNode = (
    <ModalTitle>
      <UserOutlined style={{ color: COLORS.$primaryBlue }} />
      {`${patientData?.firstName || 'Unknown'} ${patientData?.lastName || 'Patient'}`}
      <Space size={6} style={{ marginLeft: 8 }}>
        <Tag
          color={getStatusColor(queueData.status)}
          style={{
            margin: 0,
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 10.5,
            lineHeight: '18px',
            padding: '0 8px',
            textTransform: 'capitalize',
            border: 'none'
          }}
        >
          {queueData.status ? queueData.status.replace('_', ' ') : 'Not Set'}
        </Tag>
        {queueData.priority && (
          <Tag
            color={getPriorityColor(queueData.priority)}
            style={{
              margin: 0,
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 10.5,
              lineHeight: '18px',
              padding: '0 8px',
              textTransform: 'capitalize',
              border: 'none'
            }}
          >
            {queueData.priority} Priority
          </Tag>
        )}
      </Space>
    </ModalTitle>
  );

  const footerButtons = [
    <ModalButton
      key="cancel"
      onClick={onCancel}
      size="large"
    >
      Close
    </ModalButton>,
    <ModalButton
      key="copyLink"
      icon={<LinkOutlined />}
      onClick={handleCopyPatientLink}
      loading={copyingLink}
      size="large"
    >
      {isMobile ? 'Copy Link' : 'Copy Patient Link'}
    </ModalButton>,
    <ModalButton
      key="edit"
      type="primary"
      icon={<EditOutlined />}
      onClick={handleEditPatient}
      size="large"
      className="primary-button"
    >
      {isMobile ? 'Edit' : 'Edit Patient'}
    </ModalButton>
  ];

  const bodyContent = (
    <Spin spinning={loading} indicator={<HeartLoader size="32px" padding="20px" />}>
      <ModalContent>
            {/* Header Section with Name and Key Info */}
            <PatientHeaderSection>
              <Row justify="space-between" align="top" gutter={[12, 16]}>
                <Col xs={24} sm="auto">
                  {/* Status Change Dropdown */}
                  {/* Hide status dropdown for pre_registered patients - they must use the Activate button */}
                  {onStatusChange && queueData.status !== 'pre_registered' && (
                    <div>
                      <PatientIdLabel style={{ display: 'block' }}>Status:</PatientIdLabel>
                      <Dropdown menu={{ items: statusMenuItems }} trigger={['click']} placement="bottomLeft">
                        <Button 
                          size="default" 
                          icon={<TeamOutlined />}
                          style={{
                            marginTop: 6,
                            fontSize: '14px',
                            height: '36px',
                            padding: '0 16px',
                            border: `1px solid ${COLORS.$primaryBlue}`,
                            color: COLORS.$primaryBlue,
                            background: 'white',
                            borderRadius: '8px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          Change Status <DownOutlined />
                        </Button>
                      </Dropdown>
                    </div>
                  )}
                </Col>
                <Col xs={24} sm="auto">
                  <div style={{ width: '100%', minWidth: 0, maxWidth: isMobile ? 'none' : 280 }}>
                    <PatientIdLabel style={{ display: 'block' }}>Test Kit Code:</PatientIdLabel>
                    <TestingCodeCell style={{ marginTop: 6 }}>
                      <TestingCodeInput
                        size="small"
                        placeholder="Add Test Kit Code"
                        prefix={<TagOutlined style={{ fontSize: 12 }} />}
                        suffix={
                          (testingCodeDraft || '').trim() === (fullPatientData?.testingCode || '') &&
                          fullPatientData?.testingCode ? (
                            <Tooltip title="Saved">
                              <CheckCircleFilled style={{ color: COLORS.healthyGreen, fontSize: 13 }} />
                            </Tooltip>
                          ) : <span />
                        }
                        value={testingCodeDraft}
                        disabled={savingTestingCode}
                        onChange={(e) => setTestingCodeDraft(e.target.value)}
                        onPressEnter={handleSaveTestingCode}
                      />
                      <Tooltip
                        title={
                          (testingCodeDraft || '').trim() !== (fullPatientData?.testingCode || '')
                            ? 'Save test kit code'
                            : fullPatientData?.testingCode ? 'Saved' : 'Enter a code to save'
                        }
                      >
                        <TestingCodeSaveButton
                          size="small"
                          type="primary"
                          icon={<SaveOutlined />}
                          loading={savingTestingCode}
                          disabled={
                            savingTestingCode ||
                            (testingCodeDraft || '').trim() === (fullPatientData?.testingCode || '')
                          }
                          onClick={handleSaveTestingCode}
                        />
                      </Tooltip>
                    </TestingCodeCell>
                  </div>
                </Col>
              </Row>
            </PatientHeaderSection>

        {/* Patient Information */}
        <div style={sectionCard}>
          <h4 style={sectionHeading}>Patient Information</h4>
          <Row gutter={[20, 16]}>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Email" value={patientData?.emailAddress} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Phone" value={patientData?.phoneNumber} />
              {canMessage && (
              <Tooltip title={SMS_NOTIFY_ENABLED ? 'Text the patient that you are ready to begin' : 'Texting not active yet'}>
                <span style={{ display: 'inline-block', marginTop: 8 }}>
                  <Button
                    size="small"
                    icon={<MessageOutlined />}
                    loading={sendingNotify}
                    disabled={!SMS_NOTIFY_ENABLED || !patientData?.phoneNumber}
                    onClick={() => handleNotifyReady(patientData?.phoneNumber)}
                  >
                    Notify: Ready for Screening
                  </Button>
                </span>
              </Tooltip>
              )}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Date of Birth" value={formatDate(patientData?.dateOfBirth)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem
                label="Age"
                value={patientData?.dateOfBirth
                  ? `${Math.floor((new Date() - new Date(patientData.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000))} years`
                  : null}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Gender" value={patientData?.gender || patientData?.birthGender} />
            </Col>
            {(patientData?.raceEthnicity && patientData.raceEthnicity !== 'Not specified') && (
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Race / Ethnicity" value={patientData.raceEthnicity} />
              </Col>
            )}
            {patientData?.householdIncome && (
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Household Income" value={getLabel(householdIncomeLabels, patientData.householdIncome)} />
              </Col>
            )}
            {patientData?.insuranceCoverage && (
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Insurance" value={getLabel(insuranceCoverageLabels, patientData.insuranceCoverage)} />
              </Col>
            )}
            {patientData?.stateOfResidence && (
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="State of Residence" value={patientData.stateOfResidence} />
              </Col>
            )}
          </Row>
        </div>

        {/* Queue Information Section */}
        <div style={{ ...sectionCard, marginTop: 0 }}>
          <h4 style={sectionHeading}>Queue Information</h4>
          <Row gutter={[20, 16]}>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Check-in Time" value={formatDateTime(queueData.checkInTime)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Created" value={formatDateTime(queueData.createdAt)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem label="Last Updated" value={formatDateTime(queueData.updatedAt)} />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoItem
                label="Assigned Staff"
                value={staffName || (queueData.assignedStaff ? 'Loading…' : null)}
              />
            </Col>
            {queueData.matchedPatientId && (
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Matched Patient ID" value={queueData.matchedPatientId} mono />
              </Col>
            )}
          </Row>
          {queueData.notes && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #eef2f7' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 600,
                color: COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                marginBottom: 6
              }}>
                Notes
              </div>
              <div style={{
                fontSize: '14px',
                lineHeight: 1.6,
                color: COLORS.textDark,
                background: '#f8fafc',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #eef2f7'
              }}>
                {queueData.notes}
              </div>
            </div>
          )}
        </div>
        </ModalContent>
      </Spin>
  );

  return (
    <>
      <PatientDetailsModalStyles />
      {isMobile ? (
        <Drawer
          title={titleNode}
          placement="bottom"
          height="92vh"
          open={visible}
          onClose={onCancel}
          zIndex={1100}
          closable={false}
          extra={(
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onCancel}
              aria-label="Close"
            />
          )}
          styles={{
            content: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
            body: { padding: '14px 16px' }
          }}
          footer={(
            <div className="patient-details-drawer-footer">
              {footerButtons}
            </div>
          )}
        >
          {bodyContent}
        </Drawer>
      ) : (
        <Modal
          title={titleNode}
          open={visible}
          onCancel={onCancel}
          footer={footerButtons}
          width="90%"
          style={{ maxWidth: '1000px' }}
        >
          {bodyContent}
        </Modal>
      )}

      <NeedsAttentionModal
        visible={showNeedsAttentionModal}
        patient={patient}
        onSave={handleNeedsAttentionSave}
        onCancel={handleNeedsAttentionCancel}
      />
    </>
  );
};

export default PatientDetailsModal;
